import React, { useEffect, useState } from "react";
import { api } from "../utils/api";
import { PageHeader } from "../components/ui/Card";
import { Badge } from "../components/ui/Badge";
import { Button } from "../components/ui/Button";
import { TableCard, td, tdMuted, trStyle } from "../components/ui/Table";
import { EmptyState, LoadingRow } from "../components/ui/EmptyState";
import { PreviewModal } from "../components/ui/Modal";
import { colors, radius } from "../components/ui/tokens";
import { TrashIcon, EyeIcon } from "../components/ui/icons";

const FlashIcon = ({ size = 40, color = colors.textFaint }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);

const CATEGORY_LABEL: Record<string, string> = {
  cafe: "Café", verre: "Verre", balade: "Balade", manger: "Manger",
  activite: "Activité", sortie: "Sortie", rejoindre: "Rejoindre",
};

// Modération des FAST ("Flash" côté panel) — rencontres spontanées de moins de 60 minutes (voir
// OLTODO-backend/src/routes/fasts.ts). Équipe de modération (admin ou modérateur) : voir ce qui se
// passe en ce moment et supprimer si besoin, pas de suspension (contenu éphémère, self-expire).
export default function Flash() {
  const [fasts, setFasts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState<any | null>(null);

  const load = () => {
    api.get("/fasts/admin/all").then(data => { if (Array.isArray(data?.fasts)) setFasts(data.fasts); }).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const remove = (id: string) => {
    if (!window.confirm("Supprimer ce Flash ?")) return;
    api.delete(`/fasts/${id}`).then(() => setFasts(prev => prev.filter(f => f.id !== id))).catch(() => {});
  };

  return (
    <div style={{ padding: 32 }}>
      <PageHeader title="Flash" subtitle={`${fasts.length} en cours`} />

      <TableCard headers={["Organisateur", "Catégorie", "Message", "Lieu", "Audience", "Participants", "Expire", "Actions"]}>
        {loading ? (
          <tr><td colSpan={8}><LoadingRow /></td></tr>
        ) : fasts.length === 0 ? (
          <tr><td colSpan={8}><EmptyState title="Aucun Flash en cours" icon={<FlashIcon />} /></td></tr>
        ) : fasts.map(f => (
          <tr key={f.id} style={trStyle}>
            <td style={{ ...td, fontWeight: 600 }}>{f.organizer_name}</td>
            <td style={td}><span style={{ backgroundColor: colors.accentSoft, color: colors.accent, fontSize: 11, fontWeight: 700, padding: "4px 10px", borderRadius: radius.pill }}>{CATEGORY_LABEL[f.category] || f.category}</span></td>
            <td style={{ ...tdMuted, maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.message || "—"}</td>
            <td style={tdMuted}>{f.location_label || "—"}</td>
            <td style={td}><Badge tone={f.audience === "public" ? "success" : "neutral"}>{f.audience === "public" ? "Public" : "Amis"}</Badge></td>
            <td style={tdMuted}>{f.participants_count}</td>
            <td style={{ ...td, color: colors.textFaint, fontSize: 12.5 }}>{new Date(f.expires_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</td>
            <td style={td}>
              <div style={{ display: "flex", gap: 6 }}>
                <Button small variant="secondary" onClick={() => setPreview(f)}><EyeIcon size={13} /> Consulter</Button>
                <Button small variant="danger" onClick={() => remove(f.id)}><TrashIcon size={13} /> Supprimer</Button>
              </div>
            </td>
          </tr>
        ))}
      </TableCard>

      <PreviewModal
        open={!!preview}
        onClose={() => setPreview(null)}
        title={preview ? `${CATEGORY_LABEL[preview.category] || preview.category} avec ${preview.organizer_name}` : ""}
        subtitle={preview?.organizer_email}
        body={preview?.message}
        fields={preview ? [
          { label: "Audience", value: preview.audience === "public" ? "Public" : "Amis" },
          { label: "Participants", value: preview.participants_count },
          { label: "Lieu (approx.)", value: preview.location_label || "—" },
          { label: "Adresse exacte", value: preview.exact_address || "—" },
        ] : []}
      />
    </div>
  );
}
